/**
 * Push Notifications Service — Capacitor @capacitor/push-notifications
 *
 * Requires REACT_APP_ENABLE_PUSH=true (set in .env.production for mobile builds).
 * Also needs Firebase Android setup (google-services.json + google-services plugin)
 * and backend FCM_ENABLED=true with a service-account key.
 */

import { isNative } from "../utils/mobile";
import brand from "../config/brand";

import {
  getStoredPushToken,
  registerDevice,
  unregisterStoredDevice,
} from "../api/devices";
import {
  initWebPushNotifications,
  unregisterWebPush,
  cleanupWebPushNotifications,
} from "./webPushNotifications";

const PENDING_PUSH_ACTION_KEY = "ess_pending_push_action";

/** Maps deep-link path segments (backend) → notification entityType */
const PATH_SEGMENT_TO_ENTITY = {
  leaves: "leave",
  complaints: "complaint",
  advances: "advance",
  overtime: "overtime",
  permits: "permission",
  resignations: "resignation",
  payroll: "payroll",
  announcements: "announcement",
  "company-policies": "company_policy",
  requests: "request",
  appeals: "appeal",
  penalties: "penalty",
  "weekly-rest": "weekly_rest",
  attendance: "attendance",
  "wfh-requests": "wfh",
  evaluations: "evaluation",
};

let pushListeners = [];
let lastSyncedToken = null;
let initialized = false;
let initInFlight = null;
let pendingPushAction = null;

function isPushEnabled() {
  return String(process.env.REACT_APP_ENABLE_PUSH || "").toLowerCase() === "true";
}

function getPushErrorMessage(err) {
  return err?.response?.data?.message || err?.message || "unknown error";
}

function readStoredPendingPushAction() {
  try {
    const raw = localStorage.getItem(PENDING_PUSH_ACTION_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeStoredPendingPushAction(action) {
  try {
    localStorage.setItem(PENDING_PUSH_ACTION_KEY, JSON.stringify(action));
  } catch {
    // ignore quota / private mode
  }
}

function clearStoredPendingPushAction() {
  try {
    localStorage.removeItem(PENDING_PUSH_ACTION_KEY);
  } catch {
    // ignore
  }
}

function normalizePushAction(action) {
  if (!action || typeof action !== "object") return null;

  const notification = action.notification || {};
  const data =
    notification.data ||
    notification.extra ||
    action.data ||
    {};

  return {
    actionId: action.actionId || "tap",
    notification: {
      ...notification,
      data,
    },
  };
}

/**
 * Parse app deep links from FCM clickAction / Android intent.
 * Supports primary `ess://app/...` and legacy `app://ess/...`.
 */
export function parseLaunchUrlToPushAction(url) {
  if (!url || typeof url !== "string") return null;

  try {
    const parsed = new URL(url);
    const host = parsed.hostname || "";
    const scheme = parsed.protocol.replace(":", "");

    let segments = parsed.pathname.split("/").filter(Boolean);

    if (scheme === "ess" && host === "app") {
      // ess://app/leaves/123 (matches AndroidManifest)
    } else if (scheme === "app" && host === "ess") {
      // app://ess/leaves/123 (legacy backend payloads)
    } else {
      return null;
    }

    if (segments.length < 2) return null;

    const [pathSegment, entityId] = segments;
    const entityType = PATH_SEGMENT_TO_ENTITY[pathSegment] || pathSegment;

    const deepLink = url;
    const data = {
      deepLink,
      clickAction: deepLink,
      entityType,
      entityId,
    };

    return {
      actionId: "tap",
      notification: {
        data,
      },
    };
  } catch {
    return null;
  }
}

function parseDeliveredNotificationToPushAction(notification) {
  if (!notification) return null;

  const data = notification.data || notification.extra || {};
  if (!data.entityId && !data.entityType && !data.deepLink && !data.notificationId) {
    return null;
  }

  return {
    actionId: "tap",
    notification: {
      id: notification.id,
      title: notification.title,
      body: notification.body,
      data,
    },
  };
}

function pickLikelyTappedNotification(notifications = []) {
  if (!Array.isArray(notifications) || notifications.length === 0) return null;

  const actionable = notifications
    .map((notification) => ({
      notification,
      action: parseDeliveredNotificationToPushAction(notification),
    }))
    .filter((entry) => entry.action);

  if (actionable.length === 0) return null;

  // Tray order is platform-defined; last actionable item is the best cold-start guess.
  return actionable[actionable.length - 1].action;
}

/**
 * Dispatch push tap to the app. Keeps a pending copy for cold-start replay
 * when EssHome mounts after Capacitor fires pushNotificationActionPerformed.
 */
export function dispatchPushAction(action) {
  const normalized = normalizePushAction(action);
  if (!normalized) return;

  pendingPushAction = normalized;
  writeStoredPendingPushAction(normalized);

  window.dispatchEvent(
    new CustomEvent("push:action", { detail: normalized }),
  );
}

export function hasPendingPushAction() {
  return Boolean(pendingPushAction || readStoredPendingPushAction());
}

/**
 * Re-dispatch a pending tap (terminated / cold-start race with EssHome mount).
 * Call from EssHome once listeners are registered.
 */
export function replayPendingPushAction() {
  const action = pendingPushAction || readStoredPendingPushAction();
  if (!action) return false;

  pendingPushAction = action;
  window.dispatchEvent(new CustomEvent("push:action", { detail: action }));
  return true;
}

export function consumePendingPushAction() {
  const action = pendingPushAction || readStoredPendingPushAction();
  pendingPushAction = null;
  clearStoredPendingPushAction();
  return action;
}

async function checkColdStartNotification() {
  if (!isNative() || !isPushEnabled()) return;

  if (hasPendingPushAction()) {
    console.info("[Push] Cold start — pending tap already captured");
    return;
  }

  // 1) Deep link that launched the app (FCM clickAction / intent filter)
  try {
    const { App } = await import("@capacitor/app");
    const launch = await App.getLaunchUrl();
    if (launch?.url) {
      const action = parseLaunchUrlToPushAction(launch.url);
      if (action) {
        console.info("[Push] Cold start — launch URL:", launch.url);
        dispatchPushAction(action);
        return;
      }
    }
  } catch {
    // ignore
  }

  // 2) Restored tap from localStorage (previous session race)
  const stored = readStoredPendingPushAction();
  if (stored) {
    console.info("[Push] Cold start — restored pending tap from storage");
    dispatchPushAction(stored);
    return;
  }

  // 3) Fallback: delivered tray may still hold the tapped notification
  try {
    const notifications = await getDeliveredNotifications();
    const action = pickLikelyTappedNotification(notifications);
    if (action) {
      console.info("[Push] Cold start — inferred tap from delivered notifications");
      dispatchPushAction(action);
    }
  } catch {
    // ignore
  }
}

async function resolveDeviceMetadata() {
  const metadata = {
    platform: "android",
    deviceName: undefined,
    appVersion: undefined,
  };

  try {
    const { Capacitor } = await import("@capacitor/core");
    metadata.platform = Capacitor.getPlatform() || "android";
  } catch {
    // ignore
  }

  try {
    const { App } = await import("@capacitor/app");
    const info = await App.getInfo();
    metadata.appVersion = info?.version || undefined;
  } catch {
    // ignore
  }

  return metadata;
}

function currentUserId() {
  try {
    const raw = localStorage.getItem("ess_user");
    if (!raw) return "";
    return JSON.parse(raw)?.id || "";
  } catch {
    return "";
  }
}

async function syncPushTokenToBackend(token) {
  if (!token) return;

  // Include userId so switching accounts on the same phone always re-binds.
  const syncKey = `${currentUserId()}:${token}`;
  if (syncKey === lastSyncedToken) return;

  const metadata = await resolveDeviceMetadata();
  await registerDevice({
    token,
    platform: metadata.platform,
    deviceName: metadata.deviceName,
    appVersion: metadata.appVersion,
  });
  lastSyncedToken = syncKey;
  console.info("[PUSH] Token registered backend");
}

export async function unregisterPushFromBackend() {
  if (!isNative()) {
    await unregisterWebPush();
    return;
  }
  await unregisterStoredDevice();
  lastSyncedToken = null;
}

export async function initPushNotifications() {
  if (!isNative()) {
    await initWebPushNotifications();
    return;
  }

  if (!isPushEnabled()) {
    console.info(
      "[Push] Skipped — set REACT_APP_ENABLE_PUSH=true after Firebase/FCM is configured",
    );
    return;
  }

  // Prevent duplicate Capacitor listeners across login + session restore + StrictMode
  if (initialized) {
    const cached = getStoredPushToken();
    if (cached) {
      await syncPushTokenToBackend(cached).catch((err) => {
        console.error("[Push] Backend re-sync failed:", getPushErrorMessage(err));
      });
    }
    await checkColdStartNotification();
    return;
  }

  if (initInFlight) {
    return initInFlight;
  }

  initInFlight = (async () => {
    try {
      const { PushNotifications } = await import("@capacitor/push-notifications");

      const permResult = await PushNotifications.requestPermissions();
      if (permResult.receive !== "granted") {
        console.warn("[Push] Permission not granted");
        return;
      }
      console.info("[PUSH] Permission granted");

      initialized = true;

      // Android 8+: create channel BEFORE register so FCM can display immediately
      try {
        await PushNotifications.createChannel({
          id: process.env.REACT_APP_PUSH_CHANNEL_ID || "ess_high",
          name: `${brand.shortName} Notifications`,
          description: `${brand.appName} push notifications`,
          importance: 4,
          visibility: 1,
          vibration: true,
        });
      } catch {
        // createChannel may be unavailable on older plugin builds — ignore
      }

      // Listeners first so registrationError is caught if register fails.
      // Capacitor replays pushNotificationActionPerformed here on terminated cold start.
      const regListener = await PushNotifications.addListener(
        "registration",
        (token) => {
          console.info("[Push] Token registered successfully");
          window.dispatchEvent(
            new CustomEvent("push:token", { detail: { token: token.value } }),
          );
          syncPushTokenToBackend(token.value).catch((err) => {
            console.error("[Push] Backend registration failed:", getPushErrorMessage(err));
          });
        },
      );
      pushListeners.push(regListener);

      const errListener = await PushNotifications.addListener(
        "registrationError",
        (err) => {
          console.error("[Push] Registration error:", err?.error ?? "unknown");
        },
      );
      pushListeners.push(errListener);

      const receivedListener = await PushNotifications.addListener(
        "pushNotificationReceived",
        (notification) => {
          console.info("[Push] Received:", notification);
          window.dispatchEvent(
            new CustomEvent("push:received", { detail: notification }),
          );
        },
      );
      pushListeners.push(receivedListener);

      const actionListener = await PushNotifications.addListener(
        "pushNotificationActionPerformed",
        (action) => {
          console.info("[Push] Action performed:", action);
          dispatchPushAction(action);
        },
      );
      pushListeners.push(actionListener);

      await PushNotifications.register();

      const cached = getStoredPushToken();
      if (cached) {
        await syncPushTokenToBackend(cached);
      }

      await checkColdStartNotification();
    } catch (err) {
      initialized = false;
      console.error("[Push] Init failed:", getPushErrorMessage(err));
    }
  })();

  try {
    await initInFlight;
  } finally {
    initInFlight = null;
  }
}

export async function cleanupPushNotifications() {
  if (!isNative()) {
    cleanupWebPushNotifications();
    return;
  }
  await clearAllNotifications();
  pushListeners.forEach((l) => l?.remove?.());
  pushListeners = [];
  lastSyncedToken = null;
  initialized = false;
  pendingPushAction = null;
  clearStoredPendingPushAction();
}

/** Retrieve the list of delivered notifications (badge/tray) */
export async function getDeliveredNotifications() {
  if (!isNative() || !isPushEnabled()) return [];

  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");
    const { notifications } = await PushNotifications.getDeliveredNotifications();
    return notifications;
  } catch {
    return [];
  }
}

/** Clear all delivered notifications from the tray */
export async function clearAllNotifications() {
  if (!isNative() || !isPushEnabled()) return;

  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");
    await PushNotifications.removeAllDeliveredNotifications();
  } catch {
    // ignore
  }
}
